import { useEffect, useState } from "react";
import api, { MAD, apiError } from "../lib/api";
import { usePos } from "../context/PosContext";
import { PageHeader, Loader, Empty, StatCard } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { FinanceBlock } from "../components/FinanceBlock";
import { Rattachements } from "../components/Rattachements";
import { Plus, Pencil, Power, Trash2, History, Banknote, Coins, Users2, Receipt } from "lucide-react";
import { toast } from "sonner";

const EMPTY_CHARGE = { nom: "", montant: "", categorie: "Salaires", pos_id: "", salarie_id: "", jour_paiement: 1, date_debut: "", date_fin: "", commentaire: "", actif: true };
const EMPTY_SAL = { nom: "", fonction: "", salaire_mensuel: "", pos_id: "", actif: true };

function Badge({ ok, children }) {
  return <span className="tm-badge" style={{ background: ok ? "rgba(76,175,80,.15)" : "rgba(244,67,54,.15)", color: ok ? "#4CAF50" : "#F44336" }}>{children}</span>;
}

export default function ChargesFixes() {
  const { posList } = usePos();
  const posMap = Object.fromEntries(posList.map((p) => [p.id, p.nom]));
  const [tab, setTab] = useState("charges");

  const [finance, setFinance] = useState(null);
  const [charges, setCharges] = useState(null);
  const [salaries, setSalaries] = useState(null);
  const [cats, setCats] = useState([]);

  // filtres
  const [fCat, setFCat] = useState("");
  const [fPos, setFPos] = useState("");
  const [fStatut, setFStatut] = useState("");

  // modals
  const [chargeModal, setChargeModal] = useState(false);
  const [chargeForm, setChargeForm] = useState(EMPTY_CHARGE);
  const [montantModal, setMontantModal] = useState(null); // charge
  const [montantForm, setMontantForm] = useState({ montant: "", date_effet: "" });
  const [histoModal, setHistoModal] = useState(null); // {charge, rows}
  const [salModal, setSalModal] = useState(false);
  const [salForm, setSalForm] = useState(EMPTY_SAL);
  const [payModal, setPayModal] = useState(null); // salarie
  const [payForm, setPayForm] = useState({ type: "avance", montant: "", date: "", mode_paiement: "especes", commentaire: "", lier_depense: true });

  const loadFinance = () => api.get("/finance/overview").then(({ data }) => setFinance(data)).catch(() => {});
  const loadCharges = () => api.get("/charges-fixes").then(({ data }) => setCharges(data)).catch(() => setCharges([]));
  const loadSalaries = () => api.get("/salaries").then(({ data }) => setSalaries(data)).catch(() => setSalaries([]));

  useEffect(() => {
    loadFinance(); loadCharges(); loadSalaries();
    api.get("/charges-fixes/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  const refreshAll = () => { loadFinance(); loadCharges(); loadSalaries(); };

  // ---- Charges actions ----
  const openNewCharge = () => { setChargeForm({ ...EMPTY_CHARGE, date_debut: new Date().toISOString().slice(0, 10) }); setChargeModal("new"); };
  const openEditCharge = (c) => { setChargeForm({ nom: c.nom, montant: c.montant, categorie: c.categorie, pos_id: c.pos_id || "", salarie_id: c.salarie_id || "", jour_paiement: c.jour_paiement || 1, date_debut: c.date_debut || "", date_fin: c.date_fin || "", commentaire: c.commentaire || "", actif: c.actif, id: c.id }); setChargeModal("edit"); };

  const saveCharge = async (e) => {
    e.preventDefault();
    const body = { ...chargeForm, montant: parseFloat(chargeForm.montant), jour_paiement: parseInt(chargeForm.jour_paiement) || 1 };
    try {
      if (chargeModal === "edit") await api.put(`/charges-fixes/${chargeForm.id}`, body);
      else await api.post("/charges-fixes", body);
      toast.success(chargeModal === "edit" ? "Charge modifiée" : "Charge ajoutée");
      setChargeModal(false); refreshAll();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const openMontant = (c) => { setMontantForm({ montant: c.montant, date_effet: new Date().toISOString().slice(0, 10) }); setMontantModal(c); };
  const saveMontant = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/charges-fixes/${montantModal.id}/montant`, { montant: parseFloat(montantForm.montant), date_effet: montantForm.date_effet });
      toast.success("Nouveau montant appliqué"); setMontantModal(null); refreshAll();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const openHisto = async (c) => {
    const { data } = await api.get(`/charges-fixes/${c.id}/historique`);
    setHistoModal({ charge: c, rows: data });
  };

  const toggleCharge = async (c) => {
    try {
      await api.post(`/charges-fixes/${c.id}/${c.actif ? "desactiver" : "reactiver"}`);
      toast.success(c.actif ? "Charge désactivée" : "Charge réactivée"); refreshAll();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const deleteCharge = async (c) => {
    if (!window.confirm(`Supprimer la charge « ${c.nom} » ? Si elle a un historique, elle sera archivée.`)) return;
    const { data } = await api.delete(`/charges-fixes/${c.id}`);
    toast.success(data.archived ? "Charge archivée (historique conservé)" : "Charge supprimée"); refreshAll();
  };

  // ---- Salaries actions ----
  const openNewSal = () => { setSalForm(EMPTY_SAL); setSalModal("new"); };
  const openEditSal = (s) => { setSalForm({ nom: s.nom, fonction: s.fonction || "", salaire_mensuel: s.salaire_mensuel, pos_id: s.pos_id || "", actif: s.actif, id: s.id }); setSalModal("edit"); };
  const saveSal = async (e) => {
    e.preventDefault();
    const body = { ...salForm, salaire_mensuel: parseFloat(salForm.salaire_mensuel) };
    try {
      if (salModal === "edit") await api.put(`/salaries/${salForm.id}`, body);
      else await api.post("/salaries", body);
      toast.success(salModal === "edit" ? "Salarié modifié" : "Salarié ajouté");
      setSalModal(false); refreshAll();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };
  const deleteSal = async (s) => {
    if (!window.confirm(`Supprimer « ${s.nom} » ? S'il a des paiements, il sera archivé.`)) return;
    const { data } = await api.delete(`/salaries/${s.id}`);
    toast.success(data.archived ? "Salarié archivé" : "Salarié supprimé"); refreshAll();
  };
  const openPay = (s) => { setPayForm({ type: "avance", montant: "", date: new Date().toISOString().slice(0, 10), mode_paiement: "especes", commentaire: "", lier_depense: true }); setPayModal(s); };
  const savePay = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/salaries/${payModal.id}/paiements`, { ...payForm, montant: parseFloat(payForm.montant) });
      toast.success("Paiement enregistré"); setPayModal(null); refreshAll();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };
  const deletePay = async (s, p) => {
    if (!window.confirm("Supprimer ce paiement ? La dépense liée sera aussi supprimée.")) return;
    await api.delete(`/salaries/${s.id}/paiements/${p.id}`); toast.success("Paiement supprimé"); refreshAll();
  };

  const posOptions = [{ id: "", nom: "Entreprise (commune)" }, ...posList];

  // filtered charges
  const shown = (charges || []).filter((c) => {
    if (fCat && c.categorie !== fCat) return false;
    if (fPos && (c.pos_id || "") !== fPos) return false;
    if (fStatut === "actif" && !c.actif) return false;
    if (fStatut === "inactif" && c.actif) return false;
    return true;
  });
  const totalActives = (charges || []).filter((c) => c.actif).reduce((a, c) => a + (c.montant || 0), 0);

  return (
    <div>
      <PageHeader title="Charges fixes & Salaires" subtitle="Charges récurrentes, provisionnement et pilotage de la rentabilité.">
        {tab === "rattachements" ? null : tab === "charges" ? (
          <button className="tm-btn tm-btn-gold" onClick={openNewCharge} data-testid="add-charge-btn"><Plus size={16} /> Ajouter une charge fixe</button>
        ) : (
          <button className="tm-btn tm-btn-gold" onClick={openNewSal} data-testid="add-salary-btn"><Plus size={16} /> Ajouter un salarié</button>
        )}
      </PageHeader>

      {finance && <div className="mb-7"><FinanceBlock finance={finance} /></div>}

      <div className="flex gap-2 mb-5">
        <button className={`tm-btn ${tab === "charges" ? "tm-btn-gold" : "tm-btn-ghost"}`} onClick={() => setTab("charges")} data-testid="tab-charges"><Coins size={16} /> Charges fixes</button>
        <button className={`tm-btn ${tab === "salaries" ? "tm-btn-gold" : "tm-btn-ghost"}`} onClick={() => setTab("salaries")} data-testid="tab-salaries"><Users2 size={16} /> Salaires & avances</button>
        <button className={`tm-btn ${tab === "rattachements" ? "tm-btn-gold" : "tm-btn-ghost"}`} onClick={() => setTab("rattachements")} data-testid="tab-rattachements"><Coins size={16} /> Rattachements à valider</button>
      </div>

      {tab === "rattachements" && <Rattachements onDone={() => { loadFinance(); loadSalaries(); }} />}

      {tab === "charges" && (
        <div>
          <div className="mb-5 max-w-xs">
            <StatCard label="Total charges fixes actives" value={`${MAD(totalActives)}/mois`} icon={Coins} testid="total-charges-actives" />
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <select className="tm-input max-w-[180px]" value={fCat} onChange={(e) => setFCat(e.target.value)} data-testid="filter-categorie"><option value="">Toutes catégories</option>{cats.map((c) => <option key={c}>{c}</option>)}</select>
            <select className="tm-input max-w-[200px]" value={fPos} onChange={(e) => setFPos(e.target.value)} data-testid="filter-pos"><option value="">Tous points de vente</option>{posOptions.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}</select>
            <select className="tm-input max-w-[160px]" value={fStatut} onChange={(e) => setFStatut(e.target.value)} data-testid="filter-statut"><option value="">Tous statuts</option><option value="actif">Actives</option><option value="inactif">Inactives</option></select>
          </div>

          {charges === null ? <Loader /> : shown.length === 0 ? <Empty text="Aucune charge fixe." /> : (
            <div className="tm-card overflow-x-auto" data-testid="charges-table">
              <table className="tm-table">
                <thead><tr><th>Nom</th><th>Catégorie</th><th className="text-right">Montant/mois</th><th>Point de vente</th><th>Prochaine échéance</th><th>Statut</th><th className="text-right">Actions</th></tr></thead>
                <tbody>
                  {shown.map((c) => (
                    <tr key={c.id} data-testid={`charge-row-${c.id}`}>
                      <td className="font-semibold">{c.nom}{c.archive && <span className="text-xs text-[var(--tm-muted)]"> (archivée)</span>}</td>
                      <td><span className="tm-badge" style={{ background: "var(--tm-surface-2)", color: "var(--tm-muted)" }}>{c.categorie}</span></td>
                      <td className="text-right tabular">{MAD(c.montant)}</td>
                      <td className="text-[var(--tm-muted)]">{c.pos_nom}</td>
                      <td className="text-[var(--tm-muted)] whitespace-nowrap">{c.prochaine_echeance ? new Date(c.prochaine_echeance).toLocaleDateString("fr-FR") : "—"}</td>
                      <td><Badge ok={c.actif}>{c.actif ? "Active" : "Inactive"}</Badge></td>
                      <td>
                        <div className="flex items-center justify-end gap-1.5">
                          <button className="tm-btn tm-btn-ghost" title="Modifier" onClick={() => openEditCharge(c)} data-testid={`edit-charge-${c.id}`}><Pencil size={14} /></button>
                          <button className="tm-btn tm-btn-ghost" title="Changer le montant (daté)" onClick={() => openMontant(c)} data-testid={`montant-charge-${c.id}`}><Banknote size={14} /></button>
                          <button className="tm-btn tm-btn-ghost" title="Historique" onClick={() => openHisto(c)} data-testid={`histo-charge-${c.id}`}><History size={14} /></button>
                          <button className="tm-btn tm-btn-ghost" title={c.actif ? "Désactiver" : "Réactiver"} onClick={() => toggleCharge(c)} data-testid={`toggle-charge-${c.id}`}><Power size={14} /></button>
                          <button className="tm-btn tm-btn-ghost" title="Supprimer" onClick={() => deleteCharge(c)} data-testid={`delete-charge-${c.id}`}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "salaries" && (
        <div>
          {salaries === null ? <Loader /> : salaries.length === 0 ? <Empty text="Aucun salarié." /> : (
            <div className="space-y-3" data-testid="salaries-list">
              {salaries.map((s) => (
                <div key={s.id} className="tm-card p-4" data-testid={`salary-row-${s.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-lg">{s.nom} <span className="text-sm text-[var(--tm-muted)] font-normal">· {s.fonction}</span></p>
                      <p className="text-xs text-[var(--tm-muted)]">{s.pos_nom}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-5">
                      <div className="text-right"><p className="text-xs text-[var(--tm-muted)]">Salaire</p><p className="tabular font-display text-lg">{MAD(s.salaire_mensuel)}</p></div>
                      <div className="text-right"><p className="text-xs text-[var(--tm-muted)]">Versé ce mois</p><p className="tabular font-display text-lg" style={{ color: "#4CAF50" }}>{MAD(s.verses_mois)}</p></div>
                      <div className="text-right"><p className="text-xs text-[var(--tm-muted)]">Reste à verser</p><p className="tabular font-display text-lg" style={{ color: "#FF9800" }}>{MAD(s.reste_a_verser)}</p></div>
                      <div className="flex gap-1.5">
                        <button className="tm-btn tm-btn-ghost" title="Paiement / avance" onClick={() => openPay(s)} data-testid={`pay-salary-${s.id}`}><Receipt size={14} /></button>
                        <button className="tm-btn tm-btn-ghost" title="Modifier" onClick={() => openEditSal(s)} data-testid={`edit-salary-${s.id}`}><Pencil size={14} /></button>
                        <button className="tm-btn tm-btn-ghost" title="Supprimer" onClick={() => deleteSal(s)} data-testid={`delete-salary-${s.id}`}><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                  {s.paiements?.length > 0 && (
                    <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--tm-border)" }}>
                      <p className="text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-2">Historique des versements</p>
                      <div className="flex flex-wrap gap-2">
                        {s.paiements.map((p) => (
                          <span key={p.id} className="tm-badge flex items-center gap-2" style={{ background: "var(--tm-surface-2)" }}>
                            {p.type === "avance" ? "Avance" : "Salaire"} · {MAD(p.montant)} · {new Date(p.date).toLocaleDateString("fr-FR")}
                            <button onClick={() => deletePay(s, p)} className="text-[var(--tm-muted)] hover:text-[#F44336]"><Trash2 size={12} /></button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal charge create/edit */}
      <Modal open={!!chargeModal} onClose={() => setChargeModal(false)} title={chargeModal === "edit" ? "Modifier la charge fixe" : "Nouvelle charge fixe"} testid="charge-modal">
        <form onSubmit={saveCharge}>
          <Field label="Nom de la charge"><input className="tm-input" required value={chargeForm.nom} onChange={(e) => setChargeForm({ ...chargeForm, nom: e.target.value })} data-testid="charge-nom" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Montant mensuel (DH)"><input type="number" step="0.01" className="tm-input" required value={chargeForm.montant} onChange={(e) => setChargeForm({ ...chargeForm, montant: e.target.value })} data-testid="charge-montant" /></Field>
            <Field label="Catégorie"><select className="tm-input" value={chargeForm.categorie} onChange={(e) => setChargeForm({ ...chargeForm, categorie: e.target.value })} data-testid="charge-categorie">{cats.map((c) => <option key={c}>{c}</option>)}</select></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Point de vente"><select className="tm-input" value={chargeForm.pos_id} onChange={(e) => setChargeForm({ ...chargeForm, pos_id: e.target.value })} data-testid="charge-pos">{posOptions.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}</select></Field>
            <Field label="Salarié associé"><select className="tm-input" value={chargeForm.salarie_id} onChange={(e) => setChargeForm({ ...chargeForm, salarie_id: e.target.value })} data-testid="charge-salarie"><option value="">— Aucun —</option>{(salaries || []).map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}</select></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Jour de paiement"><input type="number" min="1" max="31" className="tm-input" value={chargeForm.jour_paiement} onChange={(e) => setChargeForm({ ...chargeForm, jour_paiement: e.target.value })} /></Field>
            <Field label="Date de début"><input type="date" className="tm-input" value={chargeForm.date_debut} onChange={(e) => setChargeForm({ ...chargeForm, date_debut: e.target.value })} data-testid="charge-debut" /></Field>
            <Field label="Date de fin (facultatif)"><input type="date" className="tm-input" value={chargeForm.date_fin} onChange={(e) => setChargeForm({ ...chargeForm, date_fin: e.target.value })} /></Field>
          </div>
          <Field label="Commentaire (facultatif)"><textarea className="tm-input" rows={2} value={chargeForm.commentaire} onChange={(e) => setChargeForm({ ...chargeForm, commentaire: e.target.value })} /></Field>
          <button type="submit" className="tm-btn tm-btn-gold w-full mt-2" data-testid="charge-save">Enregistrer</button>
        </form>
      </Modal>

      {/* Modal changer montant (daté) */}
      <Modal open={!!montantModal} onClose={() => setMontantModal(null)} title={`Nouveau montant · ${montantModal?.nom || ""}`} testid="montant-modal">
        <form onSubmit={saveMontant}>
          <p className="text-sm text-[var(--tm-muted)] mb-4">Le nouveau montant s'applique à partir de la date choisie, sans modifier les périodes passées. L'ancien montant est conservé dans l'historique.</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nouveau montant (DH)"><input type="number" step="0.01" className="tm-input" required value={montantForm.montant} onChange={(e) => setMontantForm({ ...montantForm, montant: e.target.value })} data-testid="montant-value" /></Field>
            <Field label="Date d'application"><input type="date" className="tm-input" required value={montantForm.date_effet} onChange={(e) => setMontantForm({ ...montantForm, date_effet: e.target.value })} data-testid="montant-date" /></Field>
          </div>
          <button type="submit" className="tm-btn tm-btn-gold w-full mt-2" data-testid="montant-save">Appliquer</button>
        </form>
      </Modal>

      {/* Modal historique */}
      <Modal open={!!histoModal} onClose={() => setHistoModal(null)} title={`Historique · ${histoModal?.charge?.nom || ""}`} testid="histo-modal">
        {(!histoModal?.rows || histoModal.rows.length === 0) ? <Empty text="Aucune modification enregistrée." /> : (
          <div className="space-y-2">
            {histoModal.rows.map((h) => (
              <div key={h.id} className="tm-card p-3 text-sm" style={{ background: "var(--tm-surface-2)" }}>
                <p><b>{MAD(h.ancienne)}</b> → <b style={{ color: "var(--tm-gold)" }}>{MAD(h.nouvelle)}</b> <span className="text-[var(--tm-muted)]">dès le {new Date(h.date_effet).toLocaleDateString("fr-FR")}</span></p>
                <p className="text-xs text-[var(--tm-muted)] mt-1">par {h.user} · {new Date(h.created_at).toLocaleString("fr-FR")}</p>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Modal salarié */}
      <Modal open={!!salModal} onClose={() => setSalModal(false)} title={salModal === "edit" ? "Modifier le salarié" : "Nouveau salarié"} testid="salary-modal">
        <form onSubmit={saveSal}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nom"><input className="tm-input" required value={salForm.nom} onChange={(e) => setSalForm({ ...salForm, nom: e.target.value })} data-testid="salary-nom" /></Field>
            <Field label="Fonction"><input className="tm-input" value={salForm.fonction} onChange={(e) => setSalForm({ ...salForm, fonction: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Salaire mensuel (DH)"><input type="number" step="0.01" className="tm-input" required value={salForm.salaire_mensuel} onChange={(e) => setSalForm({ ...salForm, salaire_mensuel: e.target.value })} data-testid="salary-montant" /></Field>
            <Field label="Point de vente"><select className="tm-input" value={salForm.pos_id} onChange={(e) => setSalForm({ ...salForm, pos_id: e.target.value })}>{posOptions.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}</select></Field>
          </div>
          {salModal === "edit" && <p className="text-xs text-[var(--tm-muted)] mb-3">La modification du salaire conserve l'historique des anciens montants (date d'effet aujourd'hui).</p>}
          <button type="submit" className="tm-btn tm-btn-gold w-full mt-2" data-testid="salary-save">Enregistrer</button>
        </form>
      </Modal>

      {/* Modal paiement/avance */}
      <Modal open={!!payModal} onClose={() => setPayModal(null)} title={`Paiement · ${payModal?.nom || ""}`} testid="pay-modal">
        <form onSubmit={savePay}>
          <p className="text-sm text-[var(--tm-muted)] mb-4">Une avance est un paiement partiel : elle réduit le « reste à verser » sans réduire la charge salariale mensuelle ({MAD(payModal?.salaire_mensuel || 0)}).</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type"><select className="tm-input" value={payForm.type} onChange={(e) => setPayForm({ ...payForm, type: e.target.value })} data-testid="pay-type"><option value="avance">Avance</option><option value="salaire">Paiement de salaire</option></select></Field>
            <Field label="Montant (DH)"><input type="number" step="0.01" className="tm-input" required value={payForm.montant} onChange={(e) => setPayForm({ ...payForm, montant: e.target.value })} data-testid="pay-montant" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><input type="date" className="tm-input" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} /></Field>
            <Field label="Mode de paiement"><select className="tm-input" value={payForm.mode_paiement} onChange={(e) => setPayForm({ ...payForm, mode_paiement: e.target.value })}><option value="especes">Espèces</option><option value="carte">Carte</option><option value="virement">Virement</option></select></Field>
          </div>
          <label className="flex items-center gap-2 mb-4 text-sm cursor-pointer">
            <input type="checkbox" checked={payForm.lier_depense} onChange={(e) => setPayForm({ ...payForm, lier_depense: e.target.checked })} data-testid="pay-lier" />
            <span>Enregistrer aussi dans les Dépenses (recommandé — reflète la sortie de trésorerie)</span>
          </label>
          <Field label="Commentaire (facultatif)"><input className="tm-input" value={payForm.commentaire} onChange={(e) => setPayForm({ ...payForm, commentaire: e.target.value })} /></Field>
          <button type="submit" className="tm-btn tm-btn-gold w-full mt-2" data-testid="pay-save">Enregistrer le paiement</button>
        </form>
      </Modal>
    </div>
  );
}
